import MoreHoriz from "@mui/icons-material/MoreHoriz";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { usePermission } from "core/hooks/usePermission";
import { sortObjectsBasedOnReferenceOrder } from "core/utils/sortObjectsBasedOnReferenceOrder";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Carrousel } from "../Carrousel";
import { ComponentErrorBoundary } from "../ComponentErrorBoundary";
import { ErrorComponent } from "../ErrorComponent";
import {
  GridAreaContainer,
  type GridAreaContainerProps,
} from "../GridAreaContainer";
import { AdvertisementWidgetSettingsDialog } from "./AdvertisementWidgetSettingsDialog";

const GRID_AREA = "ads";

function Wrapper({
  children,
  containerProps,
}: {
  children: React.ReactNode;
  containerProps?: GridAreaContainerProps;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:AdvertisementWidget",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const canManageWidgetSettings = usePermission(
    "Fixhub.AdvertisementBanners.WidgetSettings"
  );
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: GRID_AREA } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea={GRID_AREA}
        title={t("gridAreaContainer")}
        disableBottomSlot
        RightSlot={
          canManageWidgetSettings && (
            <>
              <IconButton
                onClick={() => {
                  setDialogOpen(true);
                }}
              >
                <MoreHoriz />
              </IconButton>
              <AdvertisementWidgetSettingsDialog
                open={dialogOpen}
                onClose={() => {
                  setDialogOpen(false);
                }}
              />
            </>
          )
        }
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function AdvertisementWidget({
  containerProps,
}: {
  containerProps?: GridAreaContainerProps;
}) {
  const locale = useLocale();
  const brcy = useBrcyCode();
  const { data, isError, isLoading } = useCentrixApi({
    path: "/api/app/advertisement-banner/public-list",
    parameters: {
      query: {
        Locale: locale,
        BrcyCode: brcy,
      },
    },
    fetchAll: true,
  });

  if (isLoading) {
    return (
      <Wrapper containerProps={containerProps}>
        <Skeleton variant="rectangular" height={400} />
      </Wrapper>
    );
  }
  if (isError) {
    return (
      <Wrapper containerProps={containerProps}>
        <Paper sx={{ p: 2 }}>
          <ErrorComponent apiError={isError} />
        </Paper>
      </Wrapper>
    );
  }
  const baseImages =
    data?.items?.filter(
      (entry): entry is Exclude<typeof entry, null | undefined> =>
        Boolean(entry?.id && entry?.imageSrc && entry?.altText && entry?.title)
    ) ?? [];
  const images = sortObjectsBasedOnReferenceOrder(
    baseImages,
    data?.widgetSettings?.orderedBannerIds ?? [],
    "id"
  );

  if (images.length === 0) {
    return null;
  }

  return (
    <Wrapper containerProps={containerProps}>
      <Carrousel
        images={images}
        intervalInMilliseconds={
          data?.widgetSettings?.intervalInMilliseconds ?? 3000
        }
        paperProps={{
          sx: {
            aspectRatio: "3 / 1",
            height: "auto",
            maxHeight: "calc(100vh - 250px)",
          },
        }}
      />
    </Wrapper>
  );
}
